import { useCallback, useEffect, useMemo, useState } from "react";

import { usePatientOrganizationContext } from "@/contexts/PatientOrganizationContext";
import { isVetApp } from "../messaging/helpers/isVetApp";

import {
  aggregateTemplateSends,
  buildPatientLookup,
  groupCommunicationsByPatient,
  groupCommunicationsByRecipient,
  messagesForTemplate,
  patientIdFromReference,
} from "./aggregations";
import { useClinicalCrmCommunicationsData } from "./hooks/useClinicalCrmCommunicationsData";
import {
  buildFallbackTutorSummaryFromPatientId,
  fetchVetTutorSummariesByPetIds,
} from "./helpers/crmVetTutorRecipient";
import { mapPatientEntriesToCrmPatientSummaries } from "./helpers/mapFhirPatientToCrmPatientSummary";
import { formatPatientListLabel } from "./patientLabels";
import type {
  CommunicationsFocusMode,
  CrmPatientSummary,
  FluxZapCommunicationRecord,
} from "./types";

export interface UseClinicalCrmCommunicationsViewModelParams {
  organizationId?: string;
  refreshSignal?: number;
}

/**
 * Estado e agregações para a seção de comunicações (dados FHIR + pacientes do contexto).
 */
export function useClinicalCrmCommunicationsViewModel(
  params: UseClinicalCrmCommunicationsViewModelParams = {},
) {
  const { organizationId, refreshSignal = 0 } = params;

  const [focusMode, setFocusMode] =
    useState<CommunicationsFocusMode>("patients");
  const [selectedTemplateCode, setSelectedTemplateCode] = useState<
    string | null
  >(null);

  const { patients: patientEntries, patientOrganizationLoading } =
    usePatientOrganizationContext();

  const patients = useMemo(
    () => mapPatientEntriesToCrmPatientSummaries(patientEntries),
    [patientEntries],
  );

  const {
    communications,
    batchTasks,
    isLoading: communicationsLoading,
    loadError,
    refetch,
  } = useClinicalCrmCommunicationsData(organizationId, refreshSignal);

  const isLoading = patientOrganizationLoading || communicationsLoading;
  const hasOrganization = Boolean(organizationId?.trim());

  const templateRows = useMemo(
    () => aggregateTemplateSends(communications),
    [communications],
  );

  useEffect(() => {
    if (focusMode !== "templates") return;

    if (templateRows.length === 0) {
      setSelectedTemplateCode(null);
      return;
    }

    setSelectedTemplateCode((current) => {
      if (current && templateRows.some((r) => r.templateCode === current)) {
        return current;
      }
      return templateRows[0].templateCode;
    });
  }, [focusMode, templateRows]);

  const vetApp = isVetApp();

  const basePatientLookup = useMemo(
    () => buildPatientLookup(patients),
    [patients],
  );

  const [tutorByLogicalPatientId, setTutorByLogicalPatientId] = useState<
    Map<string, CrmPatientSummary>
  >(() => new Map());

  const [tutorByPetPatientId, setTutorByPetPatientId] = useState<
    Map<string, CrmPatientSummary>
  >(() => new Map());

  useEffect(() => {
    if (!vetApp) {
      setTutorByLogicalPatientId(new Map());
      setTutorByPetPatientId(new Map());
      return;
    }

    const petIds = patients.map((p) => p.patientId).filter(Boolean);
    if (petIds.length === 0) {
      setTutorByLogicalPatientId(new Map());
      setTutorByPetPatientId(new Map());
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        const { byLogicalPatientId, byPetPatientId } =
          await fetchVetTutorSummariesByPetIds(petIds);
        if (cancelled) return;
        setTutorByLogicalPatientId(byLogicalPatientId);
        setTutorByPetPatientId(byPetPatientId);
      } catch {
        if (!cancelled) {
          setTutorByLogicalPatientId(new Map());
          setTutorByPetPatientId(new Map());
        }
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [patients, vetApp]);

  const patientLookup = useMemo(() => {
    const merged = new Map(basePatientLookup);
    for (const [id, summary] of tutorByLogicalPatientId) {
      merged.set(id, summary);
    }
    return merged;
  }, [basePatientLookup, tutorByLogicalPatientId]);

  const resolveMissingRecipient = useCallback(
    (patientId: string): CrmPatientSummary => {
      return (
        tutorByLogicalPatientId.get(patientId) ??
        buildFallbackTutorSummaryFromPatientId(patientId)
      );
    },
    [tutorByLogicalPatientId],
  );

  const patientMessageGroups = useMemo(() => {
    if (!vetApp) {
      return groupCommunicationsByPatient(patients, communications);
    }
    return groupCommunicationsByRecipient(
      patientLookup,
      communications,
      resolveMissingRecipient,
    );
  }, [
    vetApp,
    patients,
    communications,
    patientLookup,
    resolveMissingRecipient,
  ]);

  const selectedTemplateMessages = useMemo(() => {
    if (!selectedTemplateCode) return [];
    return messagesForTemplate(communications, selectedTemplateCode);
  }, [communications, selectedTemplateCode]);

  const resolvePatientForMessage = useCallback(
    (msg: FluxZapCommunicationRecord): CrmPatientSummary | undefined => {
      const id = patientIdFromReference(msg.subjectPatientReference);
      if (!id) return undefined;
      return (
        patientLookup.get(id) ??
        (vetApp ? buildFallbackTutorSummaryFromPatientId(id) : undefined)
      );
    },
    [patientLookup, vetApp],
  );

  /** Pets vinculados a cada tutor (id lógico do tutor → nomes dos pets). */
  const petNamesByTutorLogicalId = useMemo(() => {
    const map = new Map<string, string[]>();
    if (!vetApp) return map;

    for (const pet of patients) {
      const tutor = tutorByPetPatientId.get(pet.patientId);
      if (!tutor) continue;
      const key = tutor.patientId;
      const label = formatPatientListLabel(pet);
      const list = map.get(key) ?? [];
      if (!list.includes(label)) list.push(label);
      map.set(key, list);
    }
    return map;
  }, [patients, tutorByPetPatientId, vetApp]);

  return {
    focusMode,
    setFocusMode,
    selectedTemplateCode,
    setSelectedTemplateCode,
    patients,
    communications,
    batchTasks,
    patientLookup,
    templateRows,
    patientMessageGroups,
    selectedTemplateMessages,
    resolvePatientForMessage,
    vetApp,
    petNamesByTutorLogicalId,
    isLoading,
    loadError,
    refetch,
    hasOrganization,
  };
}

export type ClinicalCrmCommunicationsViewModel = ReturnType<
  typeof useClinicalCrmCommunicationsViewModel
>;
